import { addDoc, collection, getDocs, writeBatch } from "firebase/firestore";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { Close } from "@mui/icons-material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import DexcoFormFields, { getEmptyDexcoFormValues } from "./dexco_form_fields";
import { buildDexcoPost, DexcoCategory } from "../../dexco/dexco_data";
import { toTitleCaseName } from "../../../utils/title_case";

interface UploadDexcoMemberTabProps {
  onSuccess?: () => void;
}

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (
    trimmedValue.startsWith("http://") ||
    trimmedValue.startsWith("https://") ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
}

async function resequenceDexcoMembers(category: DexcoCategory, targetMemberId?: string) {
  const snapshot = await getDocs(collection(firestore, "dexco_members"));
  const orderedDocs = snapshot.docs
    .filter((memberDoc) => memberDoc.data().category === category)
    .sort((docA, docB) => {
      const rankA = Number(docA.data().rank || 0);
      const rankB = Number(docB.data().rank || 0);

      if (rankA !== rankB) {
        return rankA - rankB;
      }

      if (targetMemberId && docA.id === targetMemberId) {
        return -1;
      }

      if (targetMemberId && docB.id === targetMemberId) {
        return 1;
      }

      return String(docA.data().name || "").localeCompare(String(docB.data().name || ""));
    });

  const batch = writeBatch(firestore);
  const timestamp = new Date().toISOString();

  orderedDocs.forEach((memberDoc, index) => {
    batch.update(memberDoc.ref, {
      rank: index + 1,
      updatedAt: timestamp,
    });
  });

  await batch.commit();
}

export default function UploadDexcoMemberTab({ onSuccess }: UploadDexcoMemberTabProps) {
  const { showSnackbar } = useFeedback();
  const [formValues, setFormValues] = useState(getEmptyDexcoFormValues());
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const submitLockRef = useRef(false);

  async function handleSubmit() {
    const rank = Number(formValues.rank);

    if (submitLockRef.current) {
      return;
    }

    if (!formValues.name.trim() || !Number.isInteger(rank) || rank <= 0) {
      showSnackbar("Please fill rank and name");
      return;
    }

    if (formValues.postType === "Representative" && !formValues.postDetail.trim()) {
      showSnackbar("Please enter the representative designation");
      return;
    }

    if (formValues.postType === "Deanery President" && !formValues.deaneryName.trim()) {
      showSnackbar("Please select the deanery");
      return;
    }

    try {
      submitLockRef.current = true;
      setIsSaving(true);
      const timestamp = new Date().toISOString();
      let finalImage = formValues.img.trim();

      if (selectedImage) {
        const storageRef = ref(storage, `dexco_members/${Date.now()}-${selectedImage.name}`);
        await uploadBytes(storageRef, selectedImage);
        finalImage = await getDownloadURL(storageRef);
      }

      const newMemberRef = await addDoc(collection(firestore, "dexco_members"), {
        category: formValues.category,
        rank,
        name: toTitleCaseName(formValues.name),
        postType: formValues.postType,
        postDetail: formValues.postDetail.trim(),
        deaneryName: formValues.deaneryName.trim(),
        post: buildDexcoPost(
          formValues.postType,
          formValues.postDetail,
          formValues.deaneryName,
        ),
        place: formValues.place.trim(),
        img: normalizeUrl(finalImage),
        createdAt: timestamp,
        updatedAt: timestamp,
      });

      await resequenceDexcoMembers(formValues.category, newMemberRef.id);

      setFormValues(getEmptyDexcoFormValues());
      setSelectedImage(null);
      showSnackbar("DEXCO member added successfully");
      onSuccess?.();
    } catch (error) {
      console.error("Error adding dexco member", error);
      showSnackbar("Failed to add DEXCO member");
    } finally {
      setIsSaving(false);
      submitLockRef.current = false;
    }
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", borderRadius: 3, boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)" }}>
            <CardContent sx={{ p: 3 }}>
              <DexcoFormFields formValues={formValues} setFormValues={setFormValues} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", borderRadius: 3, boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)" }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" flexDirection="column" gap={2.5}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={formValues.img}
                  onChange={(event) => {
                    setFormValues((prev) => ({ ...prev, img: event.target.value }));
                    if (event.target.value) {
                      setSelectedImage(null);
                    }
                  }}
                  helperText="Use either an image URL or upload an image below."
                />
                <FormControl fullWidth>
                  <FormLabel>Upload Member Image</FormLabel>
                  <input
                    type="file"
                    accept="image/*"
                    style={{
                      border: "1px solid rgb(181, 176, 176)",
                      width: "96%",
                      paddingLeft: 8,
                      paddingRight: 8,
                      paddingTop: 16,
                      paddingBottom: 16,
                      borderRadius: 5,
                      marginTop: 8,
                    }}
                    onChange={(event) => {
                      if (event.target.files?.[0]) {
                        setSelectedImage(event.target.files[0]);
                        setFormValues((prev) => ({ ...prev, img: "" }));
                      }
                    }}
                  />
                  {selectedImage ? (
                    <Box mt={2} position="relative" display="inline-block">
                      <img
                        src={URL.createObjectURL(selectedImage)}
                        alt="DEXCO preview"
                        style={{ maxWidth: "220px", maxHeight: "220px", borderRadius: 8 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setSelectedImage(null)}
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          color: "#ffffff",
                          backgroundColor: "rgba(0,0,0,0.5)",
                        }}
                      >
                        <Close />
                      </IconButton>
                    </Box>
                  ) : null}
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSubmit} disabled={isSaving} sx={{ minWidth: 220 }}>
            {isSaving ? "Saving..." : "Save Member"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
